/**
 * WallScene — room photo + draggable/zoomable frame overlay (the new UI piece).
 * Renders each slot's preview (local objectURL first, then the Cloudinary smart
 * crop once uploaded). Pan/zoom/focus is imperative via refs for smoothness.
 */
import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef, useCallback } from "react";
import { wallLayout, frameWebp, frameByName, wallGridWidthPctForAspect, sceneCx, sceneCxM, sceneCy, sceneCyM, scenePosM, sceneOffYM, sceneMaxGwM, sceneHfM, sceneMAr, SCENE_IMG_ASPECT, sceneLight, sceneAmbient, sceneMobileZoom, sceneFocusZoom } from "./data";

const LR_ICON = (
  <svg viewBox="0 0 24 24">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9.5" x2="12" y2="14" />
    <line x1="12" y1="17.1" x2="12" y2="17.2" />
  </svg>
);
const LR_TIP = "לתמונה זו רזולוציה נמוכה מהמומלץ. מומלץ להעלות תמונה באיכות גבוהה יותר לתוצאת הדפסה חדה.";

const isDesktop = () => (typeof window !== "undefined" ? window.innerWidth >= 761 : true);

const WallScene = forwardRef(function WallScene({ count, frameName, env, slots, rows, brick, onSlotClick, onZoom }, ref) {
  const wallRef = useRef(null);
  const sceneRef = useRef(null);
  const zoneRef = useRef(null);
  const frRefs = useRef([]);
  const defaultZoom = isDesktop() ? 1.12 : sceneMobileZoom(env); // per-scene mobile zoom
  const z = useRef(defaultZoom);
  const panX = useRef(0);
  const panY = useRef(0);
  const [loadedSrc, setLoadedSrc] = useState({}); // crop URL -> painted (avoids raw-preview jump)
  const [tip, setTip] = useState(null); // low-res tip bubble {x,y} in wall coords, or null
  // The scene photo is object-fit:cover, so how big the frames read depends on the
  // wall box's aspect (a tall mobile wall magnifies the photo). Track it to correct --gw.
  const [wallAspect, setWallAspect] = useState(isDesktop() ? 1.5 : 0.6);
  useEffect(() => {
    const el = wallRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setWallAspect(r.width / r.height);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const clampPan = useCallback(() => {
    const w = wallRef.current;
    if (!w) return;
    const mx = (w.clientWidth * (z.current - 1)) / 2;
    const my = (w.clientHeight * (z.current - 1)) / 2;
    // The scene rests shifted down by --scene-offy on mobile; keep pan so the TOTAL shift
    // (pan + rest offset) stays within the zoom overflow, or the room's edge would show.
    const offy = isDesktop() ? 0 : (sceneOffYM(env) / 100) * w.clientHeight;
    panX.current = Math.max(-mx, Math.min(mx, panX.current));
    panY.current = Math.max(-my - offy, Math.min(my - offy, panY.current));
  }, [env]);

  const apply = useCallback(() => {
    const s = sceneRef.current;
    if (!s) return;
    clampPan();
    s.style.setProperty("--z", z.current);
    s.style.setProperty("--px", panX.current / 16 + "rem");
    s.style.setProperty("--py", panY.current / 16 + "rem");
  }, [clampPan]);

  const setZoom = useCallback((v) => {
    z.current = Math.min(2.6, Math.max(1, Math.round(v * 100) / 100));
    apply();
    setTip(null);
    if (onZoom) onZoom(z.current > defaultZoom + 0.05);
  }, [apply, onZoom, defaultZoom]);

  const clearFocus = useCallback(() => {
    frRefs.current.forEach((el) => el && el.classList.remove("focus"));
    if (zoneRef.current) zoneRef.current.classList.remove("focusing");
  }, []);

  // Low-res badge tap -> anchor a tip bubble at the badge's position, in WALL coords so it
  // renders OUTSIDE the scaled scene (readable, not shrunk by the scene zoom).
  const onBadge = useCallback((e) => {
    e.stopPropagation();
    const wall = wallRef.current;
    if (!wall) return;
    const wr = wall.getBoundingClientRect();
    const br = e.currentTarget.getBoundingClientRect();
    setTip({ x: br.left + br.width / 2 - wr.left, y: br.bottom - wr.top });
  }, []);

  useImperativeHandle(ref, () => ({
    focusTile(i) {
      const wall = wallRef.current, scene = sceneRef.current, fr = frRefs.current[i];
      if (!wall || !scene || !fr) return;
      clearFocus();
      fr.classList.add("focus");
      if (zoneRef.current) zoneRef.current.classList.add("focusing");
      scene.classList.add("zooming");
      const wr = wall.getBoundingClientRect(), sr = fr.getBoundingClientRect();
      const dx = sr.left + sr.width / 2 - (wr.left + wr.width / 2);
      const dy = sr.top + sr.height / 2 - (wr.top + wr.height / 2);
      // Account for the resting --scene-offy (mobile) so the focused tile centers exactly.
      const offy = isDesktop() ? 0 : (sceneOffYM(env) / 100) * wr.height;
      const ux = (dx - panX.current) / z.current, uy = (dy - panY.current - offy) / z.current;
      // Adaptive focus zoom: pull the tapped tile to a consistent on-screen size. A fixed
      // zoom left small-tile walls (the 16-diamond on the big stone wall) too far out. Scale
      // to the tile's measured size, targeting a fraction of the limiting viewport dim (width
      // on portrait mobile, height on landscape desktop). Never below 2.9 (well-sized walls
      // stay identical); capped so it never over-zooms a tiny tile.
      const dsk = isDesktop();
      const vp = dsk ? wr.height : wr.width;
      const tileBase = sr.width / z.current || 1; // tile width at zoom 1
      let fz = Math.max(2.9, Math.min((dsk ? 0.25 : 0.37) * vp / tileBase, dsk ? 4.6 : 5));
      // Per-scene extra pull-in (e.g. the modern 4-across gallery reads too far out on
      // frame click) — apply the scene multiplier, with a slightly higher hard cap.
      fz = Math.min(fz * sceneFocusZoom(env), dsk ? 5.6 : 6.2);
      const ty = -wr.height * 0.08;
      z.current = fz;
      panX.current = -fz * ux;
      panY.current = ty - offy - fz * uy;
      scene.style.setProperty("--z", z.current);
      scene.style.setProperty("--px", panX.current / 16 + "rem");
      scene.style.setProperty("--py", panY.current / 16 + "rem");
      if (onZoom) onZoom(true);
    },
    unfocus() {
      clearFocus();
      z.current = defaultZoom;
      panX.current = 0;
      panY.current = 0;
      if (sceneRef.current) sceneRef.current.classList.remove("zooming");
      apply();
      if (onZoom) onZoom(false);
    },
  }), [apply, clearFocus, defaultZoom, env, onZoom]);

  useEffect(() => {
    apply();
    const wall = wallRef.current, scene = sceneRef.current;
    if (!wall) return undefined;
    let drag = false, sx = 0, sy = 0, bx = 0, by = 0;
    const dstart = (e) => {
      setTip(null); // any scene interaction dismisses the low-res tip
      if (e.target.closest && e.target.closest(".slot")) return;
      drag = true; sx = e.clientX; sy = e.clientY; bx = panX.current; by = panY.current;
      wall.classList.add("grabbing");
      if (scene) scene.classList.remove("zooming");
    };
    const dmove = (e) => { if (!drag) return; panX.current = bx + (e.clientX - sx); panY.current = by + (e.clientY - sy); apply(); };
    const dend = () => { if (drag) { drag = false; wall.classList.remove("grabbing"); } };
    const desktop = isDesktop();
    const onWheel = (e) => { e.preventDefault(); setZoom(z.current + (e.deltaY < 0 ? 0.15 : -0.15)); };
    const onDown = (e) => {
      dstart(e);
      if (drag && !desktop) { try { wall.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ } }
      if (drag && desktop) e.preventDefault();
    };
    if (desktop) {
      wall.addEventListener("mousedown", onDown);
      window.addEventListener("mousemove", dmove);
      window.addEventListener("mouseup", dend);
      wall.addEventListener("wheel", onWheel, { passive: false });
    } else {
      wall.addEventListener("pointerdown", onDown);
      wall.addEventListener("pointermove", dmove);
      wall.addEventListener("pointerup", dend);
      wall.addEventListener("pointercancel", dend);
    }
    return () => {
      if (desktop) {
        wall.removeEventListener("mousedown", onDown);
        window.removeEventListener("mousemove", dmove);
        window.removeEventListener("mouseup", dend);
        wall.removeEventListener("wheel", onWheel);
      } else {
        wall.removeEventListener("pointerdown", onDown);
        wall.removeEventListener("pointermove", dmove);
        wall.removeEventListener("pointerup", dend);
        wall.removeEventListener("pointercancel", dend);
      }
    };
  }, [apply, setZoom]);

  const wl = wallLayout(count);
  // "flat" frames (shadow:false) read matte: no drop-shadow + window sits
  // slightly up-left of center, so the photo needs the same nudge.
  const flat = frameByName(frameName).shadow === false;
  // Grid width from REAL sizes (8×8" frames vs the room's scale), corrected for the wall
  // box's cover-crop. A `rows` shape renders as a HORIZONTAL diamond — each entry is a
  // vertically-centred COLUMN, so the number of columns (rows.length) drives the width.
  // --cx nudges it over the empty wall. See WALL_SCENE_PROPORTIONS.md.
  const shape = !!(rows && rows.length);        // horizontal diamond (columns)
  const shapeB = !!(brick && brick.length);     // staggered brick (offset rows)
  const numCols = shapeB ? Math.max(...brick) : shape ? rows.length : wl.cols;
  // FIXED cap (not /zoom) so lowering a scene's zoom actually shrinks the piece + shows
  // more room. Mobile default 72; wide scenes raise it (sceneMaxGwM) so a grid the portrait
  // crop would otherwise clamp too small reads at a faithful size. Desktop fixed 84.
  const gwCap = isDesktop() ? 84 : sceneMaxGwM(env);
  // Mobile may use a TALLER gen-fill photo (env-*-m.webp, aspect mAr) so the portrait crop
  // magnifies far less — the grid sizing then uses that photo's real aspect.
  const mAr = sceneMAr(env);
  const useMVar = !isDesktop() && !!mAr;
  const imgFile = useMVar ? `${env}-m` : env;
  const gwPct = Math.min(wallGridWidthPctForAspect(numCols, env, wallAspect, useMVar ? mAr : SCENE_IMG_ASPECT), gwCap);
  // Mobile cover-crops the scene to its middle -> horizontal (and useful vertical) position
  // shifts, so nudge per-breakpoint (see data.js). Desktop/thumb use cx/cy; phone cxM/cyM.
  const desktop = isDesktop();
  const gx = desktop ? sceneCx(env) : sceneCxM(env);
  const gy = desktop ? sceneCy(env) : sceneCyM(env);
  // Desktop shows ~the full 3:2 photo (centre crop); mobile crops to a narrow centre band,
  // so recenter that band per-scene (scenePosM) to keep an off-centre anchor in frame.
  const wallPosX = desktop ? 50 : scenePosM(env);
  const light = sceneLight(env);
  // Light vars live on the .wall so the scene-level grade (.wall::before/after) can use
  // them — it grades the WHOLE scene (room + frames), not a box behind the frames.
  // --scene-offy shifts the whole scene (room + frames) down on mobile only; the scene's
  // transform folds it in on top of the live pan/zoom. Desktop stays 0.
  const wallStyle = { "--light-angle": light.angle + "deg", "--light-warm": light.warm, "--light-dim": light.dim, "--scene-offy": (desktop ? 0 : sceneOffYM(env)) + "%", "--wall-hf": sceneHfM(env) };
  const gridStyle = {
    "--cols": numCols,
    "--gap": wl.gap / 16 + "rem",
    "--gw": gwPct + "%",
    "--cx": gx + "%",
    "--cy": gy + "%",
    "--ambient": sceneAmbient(env),
    "--frameimg": `url('${frameWebp(frameName)}')`,
    "--sw": (slots.__sw || 74) + "%",
  };

  // One frame cell (shared by the flat grid + the per-row diamond layout).
  const renderCell = (i) => {
    const cell = slots[i];
    const hasEntry = !!cell;
    const hasCrop = !!(cell && cell.src);
    const ready = hasCrop && loadedSrc[cell.src];
    return (
      <div className="fr" key={i} ref={(el) => (frRefs.current[i] = el)}>
        <div
          className={`slot${ready ? " filled" : ""}`}
          role="button"
          tabIndex={0}
          aria-label="הוספת תמונה למסגרת"
          onClick={() => onSlotClick(i)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSlotClick(i); } }}
        >
          {cell && cell.uploading ? (
            // Not on the server yet — plain white + gray spinner, never the local photo.
            <span className="slotupload"><span className="up-spin" /></span>
          ) : hasCrop ? (
            <>
              <img
                src={cell.src}
                alt=""
                draggable={false}
                onLoad={() => setLoadedSrc((p) => (p[cell.src] ? p : { ...p, [cell.src]: true }))}
                style={{ opacity: ready ? 1 : 0, objectPosition: cell.objPos || "50% 50%", filter: cell.bw ? "grayscale(1)" : undefined }}
              />
              {cell.low && ready ? (
                <button type="button" className="lrbadge" aria-label="תמונה באיכות נמוכה" onClick={onBadge}>{LR_ICON}</button>
              ) : null}
              {!ready ? <span className="slotshimmer" /> : null}
            </>
          ) : hasEntry ? (
            <span className="slotshimmer" />
          ) : (
            <div className="plus" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="wall" ref={wallRef} style={wallStyle}>
      <div className="scene" ref={sceneRef}>
        <img className="wall-img" src={`/assets/shopping/env/${imgFile}.webp`} alt="קיר גלריה" loading="eager" draggable={false} style={{ objectPosition: `${wallPosX}% 50%` }} />
        <div className="zone" ref={zoneRef}>
          <div className={`grid${flat ? " flat" : ""}${shapeB ? " brick" : shape ? " hdiamond" : ""}`} style={gridStyle}>
            {shapeB
              ? brick.map((n, r) => {
                  const start = brick.slice(0, r).reduce((a, b) => a + b, 0);
                  return (
                    <div className="grow" key={r}>
                      {Array.from({ length: n }).map((_, k) => renderCell(start + k))}
                    </div>
                  );
                })
              : shape
              ? rows.map((n, c) => {
                  const start = rows.slice(0, c).reduce((a, b) => a + b, 0);
                  return (
                    <div className="gcol" key={c}>
                      {Array.from({ length: n }).map((_, k) => renderCell(start + k))}
                    </div>
                  );
                })
              : Array.from({ length: count }).map((_, i) => renderCell(i))}
          </div>
          <div className="spot" />
        </div>
      </div>
      <div className="zoomctl">
        <button type="button" aria-label="הגדלה" onClick={() => setZoom(z.current + 0.2)}>+</button>
        <button type="button" aria-label="הקטנה" onClick={() => setZoom(z.current - 0.2)}>−</button>
      </div>
      {tip ? (
        <div className="lrtip" style={{ left: tip.x + "px", top: tip.y + "px" }} onClick={(e) => { e.stopPropagation(); setTip(null); }}>
          <div className="lrtip-b">{LR_TIP}</div>
        </div>
      ) : null}
    </div>
  );
});

export default WallScene;
